// Reforço de ruptura: itens que estavam ZERADOS e já tiveram pedido ENVIADO com a
// quantidade simples (antes da regra do dobro). Esses pedidos em trânsito ficam
// abaixo do dobro e o item re-rompe dentro do lead time. Como o app assume um
// pedido aberto por item, eles somem do plano/emissão — este diagnóstico apenas
// IDENTIFICA a necessidade de um pedido complementar (reforço). Puro/determinístico.
//
// Transitório: some sozinho conforme os pedidos enviados são recebidos.

import { isRupturaAcionavel } from './replenishment'
import type { OrderStatus, ProductRow } from '../types'

export interface ReforcoItem {
  id: string
  sku: string
  name: string
  fornecedor_nome: string
  current_stock: number
  /** Quantidade já em pedido aberto (sinal do enviado). */
  open_qty: number
  /** Alvo dobrado = 2× (ponto de ressuprimento − estoque), arredondado p/ cima. */
  alvoDobrado: number
  /** Quanto falta comprar para completar o dobro. */
  reforco: number
  /** reforco × custo unitário. */
  valorReforco: number
}

export interface ReforcoRuptura {
  itens: ReforcoItem[]
  totalReforco: number
  totalValor: number
}

/** Campos do ProductRow que a análise lê (subset para testabilidade). */
type Row = Pick<
  ProductRow,
  'id' | 'sku' | 'name' | 'current_stock' | 'kanban_status' | 'active_order_status'
  | 'open_qty' | 'reorder_point' | 'cost' | 'suppliers'
>

export function computeReforcoRuptura(rows: ReadonlyArray<Row>): ReforcoRuptura {
  const itens: ReforcoItem[] = []
  for (const r of rows) {
    if (!isRupturaAcionavel(r.current_stock, r.kanban_status)) continue
    // Só pedidos ENVIADOS (o usuário pediu este caso); precisa ter qtd em aberto.
    if (r.active_order_status !== ('Enviado' as OrderStatus)) continue
    if ((r.open_qty ?? 0) <= 0) continue

    const baseGross = Math.max(0, (r.reorder_point ?? 0) - r.current_stock)
    const alvoDobrado = Math.ceil(baseGross * 2)
    const reforco = Math.max(0, alvoDobrado - (r.open_qty ?? 0))
    if (reforco <= 0) continue // o que já foi enviado cobre o dobro

    itens.push({
      id: r.id,
      sku: r.sku,
      name: r.name,
      fornecedor_nome: r.suppliers?.name ?? 'Sem fornecedor vinculado',
      current_stock: r.current_stock,
      open_qty: r.open_qty ?? 0,
      alvoDobrado,
      reforco,
      valorReforco: reforco * (r.cost ?? 0),
    })
  }
  // Maior reforço primeiro (mais relevante no topo).
  itens.sort((a, b) => b.reforco - a.reforco)
  return {
    itens,
    totalReforco: itens.reduce((s, i) => s + i.reforco, 0),
    totalValor: itens.reduce((s, i) => s + i.valorReforco, 0),
  }
}
